"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { type Store } from "@/actions/schemas/stores";
import { getStore } from "@/actions/store";
import { StoreProvider } from "@/contexts/store";
import { VitrineProvider } from "@/contexts/vitrine-provider";

type StoreProvidersProps = {
  store: Store;
  identifier: string;
  children: ReactNode;
};

const StoreRefetchingContext = createContext(false);

export const useStoreRefetching = () => useContext(StoreRefetchingContext);

export const StoreProviders = ({
  store: initialStore,
  identifier,
  children,
}: StoreProvidersProps) => {
  const [store, setStore] = useState(initialStore);
  const pathname = usePathname();
  const [refetchedPathname, setRefetchedPathname] = useState<string | null>(
    null
  );
  const isRefetching = refetchedPathname !== pathname;

  useEffect(() => {
    let isMounted = true;

    getStore({ identifier }, { revalidate: 0 }).then((result) => {
      if (!isMounted) return;

      if (result.data) {
        setStore(result.data);
      }

      setRefetchedPathname(pathname);
    });

    return () => {
      isMounted = false;
    };
  }, [identifier, pathname]);

  return (
    <StoreRefetchingContext.Provider value={isRefetching}>
      <VitrineProvider store={store} identifier={identifier}>
        <StoreProvider store={{ ...store, identifier }}>
          {children}
        </StoreProvider>
      </VitrineProvider>
    </StoreRefetchingContext.Provider>
  );
};
